import React from "react";
import { Box, Typography, Avatar } from "@mui/material";
import styles from "./UpcomingAppointmentCard.module.scss";
import { useTranslation } from "react-i18next";
import { CalenderIcon } from "../../../components/icon-components/CalenderIcon";
import { ClockIcon } from "../../../components/icon-components/ClockIcon";
import { useGetProfilePictureQuery } from "../services/GetProfilePictureApiSlice";

type UpcomingAppointmentCardProps = {
  providerFhirId: string;
  physicianName: string;
  specialty: string;
  healthSystem: string;
  appointmentDate: string;
  appointmentTime: string;
};

const UpcomingAppointmentCard: React.FC<UpcomingAppointmentCardProps> = ({
  providerFhirId,
  physicianName,
  specialty,
  healthSystem,
  appointmentDate,
  appointmentTime,
}) => {
  const { t } = useTranslation();
  const skip = !providerFhirId;
  const {
    data: profilePictureData,
    isLoading,
    isError,
  } = useGetProfilePictureQuery({ providerFhirId },  { skip });

  // Only use the fetched URL once it's ready and error-free
  const avatarSrc =
    !isLoading && !isError && profilePictureData?.imageUrl
      ? profilePictureData.imageUrl
      : undefined;

  return (
    <Box className={styles.card}>
      {/* Header */}
      <Box className={styles.header}>
        <Typography
          variant="bodySmall"
          sx={{
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "0.72px",
          }}
        >
          {t("spotLightCarousel.upcomingAppointments")}
        </Typography>
      </Box>

      {/* Content */}
      <Box className={styles.content}>
        <Box className={styles.doctorInfo}>
          <Avatar
            src={avatarSrc}
            alt={physicianName}
            className={styles.avatar}
          />
          <Box>
            <Typography variant="body1">{physicianName}</Typography>
            <Typography
              variant="body2"
              className={styles.details}
            >
              {specialty} <span className={styles.dot}>|</span> {healthSystem}
            </Typography>
          </Box>
        </Box>

        {/* Date & Time */}
        <Box className={styles.appointment}>
          <Box className={styles.dateTimeItem}>
            <CalenderIcon />
            <Typography variant="body2">{appointmentDate}</Typography>
          </Box>
          <Box className={styles.dateTimeItem}>
            <ClockIcon />
            <Typography variant="body2">{appointmentTime}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UpcomingAppointmentCard;
